import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { PromptTypes } from 'generated/graphql-types';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ExtensionPrivileges } from 'src/app/pages/extension-details/models/extension-privileges';
import { promptItemDialogSubmit } from 'src/app/pages/extension-details/state/extension-detail.actions';
import { AudioPlaylistPlayerService } from '../audio-playlist-player/services/audio-playlist-player.service';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from '../confirmation-dialog/enumerations/dialog-size.enum';
import { AutoAttendantPromptItemView } from './models/auto-attendant-prompt-item.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-auto-attendant-prompt-list',
    styleUrls: ['./auto-attendant-prompt-list.component.scss'],
    templateUrl: './auto-attendant-prompt-list.component.html',
    standalone: false
})
export class AutoAttendantPromptListComponent implements ControlValueAccessor, OnDestroy {
   @Input() accountId: number;

   @Input() callState: CallState;

   @Input() extensionPrivileges: ExtensionPrivileges;

   @Input() firstPromptNotSkippable: FormControl;

   @Output() addExistingPromptClick = new EventEmitter<void>();

   @Output() addNewPromptClick = new EventEmitter<PromptTypes>();

   autoAttendantPromptItems: AutoAttendantPromptItemView[] = [];

   get activeAutoAttendantPromptItems(): AutoAttendantPromptItemView[] {
      return this.autoAttendantPromptItems.filter((promptItem) => {
         return !promptItem.isDeleted;
      });
   }

   fileTokens: (string | undefined)[] = [];

   disabled = false;

   onTouched: () => void;

   private destroyed$ = new Subject<void>();

   private onChange: (value: Array<AutoAttendantPromptItemView> | null) => void;

   showAudioPlayer: boolean;

   currentPlaylistIndex: number;

   isPaused: boolean;

   isLoading: boolean;

   showError: boolean;

   autoPlayAll: boolean;

   playlistIndexSubscription: Subscription;

   isPausedSubscription: Subscription;

   isLoadingSubscription: Subscription;

   showErrorSubscription: Subscription;

   autoPlayAllSubscription: Subscription;

   promptTypes: typeof PromptTypes = PromptTypes;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      public audioService: AudioPlaylistPlayerService
   ) {
      control.valueAccessor = this;
      this.actions$.pipe(ofType(promptItemDialogSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         const promptToAdd: AutoAttendantPromptItemView = {
            autoAttendantPromptId: 0,
            extensionAutoAttendantId: 0,
            isDeleted: false,
            prompt: {
               file: {
                  fileToken: action.promptItem.file?.fileToken ?? '',
               },
               name: action.promptItem.name ?? '',
               promptId: action.promptItem.promptId,
            },
            sortId: (this.activeAutoAttendantPromptItems?.length ?? 0) + 1,
         };
         this.autoAttendantPromptItems.push(promptToAdd);
         this.fileTokens = this.autoAttendantPromptItems
            .filter((item) => !item.isDeleted)
            .map((item) => item.prompt.file?.fileToken);
         this.audioService.setPlaylistIndex(-1);
         this.onChange(this.autoAttendantPromptItems);
         this.changeDetectorRef.markForCheck();
      });
      this.subscribeToAudioService();
      this.showAudioPlayer = true;
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this prompt?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDeleteClick(index: number): void {
      this.deleteConfirmationDialog()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((confirmed) => {
            if (confirmed) {
               this.autoAttendantPromptItems[index].isDeleted = true;
               this.reOrderActivePrompts();
               this.fileTokens = this.autoAttendantPromptItems
                  .filter((item) => !item.isDeleted)
                  .map((item) => item.prompt.file?.fileToken);
               this.changeDetectorRef.markForCheck();
               this.audioService.setPlaylistIndex(-1);
               this.onChange(this.autoAttendantPromptItems);
            }
         });
   }

   drop(event: CdkDragDrop<AutoAttendantPromptItemView[]>): void {
      moveItemInArray(this.autoAttendantPromptItems, event.previousIndex, event.currentIndex);
      this.reOrderActivePrompts();

      this.autoAttendantPromptItems = [...this.autoAttendantPromptItems];
      this.fileTokens = this.autoAttendantPromptItems
         .filter((item) => !item.isDeleted)
         .map((item) => item.prompt.file?.fileToken);
      this.audioService.setPlaylistIndex(-1);
      if (this.autoPlayAll) this.audioService.setAutoPlayAll(false);
      this.onChange(this.autoAttendantPromptItems);
      this.changeDetectorRef.markForCheck();
   }

   writeValue(promptItems: AutoAttendantPromptItemView[]): void {
      if (promptItems?.length > 0) {
         this.autoAttendantPromptItems = this.sortPrompts(
            //promptItems is readOnly, need to shallow copy in order to sort
            promptItems.map((item) => {
               return { ...item, isDeleted: false };
            })
         );
      } else {
         this.autoAttendantPromptItems = [];
      }
      this.fileTokens = this.autoAttendantPromptItems
         .filter((item) => !item.isDeleted)
         .map((item) => item.prompt.file?.fileToken);
      this.audioService.setPlaylistIndex(-1);
      this.changeDetectorRef.markForCheck();
   }

   registerOnChange(onChange: (value: Array<AutoAttendantPromptItemView> | null) => void): void {
      this.fileTokens = this.autoAttendantPromptItems
         .filter((item) => !item.isDeleted && item.prompt.file?.fileToken)
         .map((item) => item.prompt.file?.fileToken);
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   onAddExistingPromptClick() {
      this.addExistingPromptClick.emit();
   }

   onPlayAllClick() {
      this.audioService.setAutoPlayAll(true);
      this.changeDetectorRef.markForCheck();
   }

   onStopAllClick() {
      this.audioService.setAutoPlayAll(false);
      this.changeDetectorRef.markForCheck();
   }

   onPlayClick(promptArrayIndex: number, event: MouseEvent) {
      if (!this.showAudioPlayer) {
         this.audioService.setIsLoading(true);
         this.audioService.setPlaylistIndex(promptArrayIndex);
         this.showAudioPlayer = true;
      }
      if (promptArrayIndex === this.currentPlaylistIndex) {
         if (event) {
            return;
         } // avoid infinite loop if user double clicks play/pause
         this.audioService.setIsPaused(!this.isPaused);
      } else {
         this.audioService.setPlaylistIndex(promptArrayIndex);
      }
   }

   subscribeToAudioService() {
      this.playlistIndexSubscription = this.audioService.currentPlaylistIndex
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            this.currentPlaylistIndex = value;
            this.changeDetectorRef.markForCheck();
         });
      this.isPausedSubscription = this.audioService.isPaused.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isPaused = value;
         this.changeDetectorRef.markForCheck();
      });
      this.isLoadingSubscription = this.audioService.isLoading.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isLoading = value;
         this.changeDetectorRef.markForCheck();
      });
      this.showErrorSubscription = this.audioService.showError.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.showError = value;
         this.changeDetectorRef.markForCheck();
      });
      this.autoPlayAllSubscription = this.audioService.autoPlayAll
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            this.autoPlayAll = value;
            this.changeDetectorRef.markForCheck();
         });
   }

   onAddNewPromptClick(promptType: PromptTypes) {
      this.addNewPromptClick.emit(promptType);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   private sortPrompts(promptsList: AutoAttendantPromptItemView[]): AutoAttendantPromptItemView[] {
      return promptsList.sort((p1, p2) => {
         if (!p1.sortId && !p2.sortId) {
            return 0;
         }

         if (!p1.sortId || p1.sortId > p2.sortId) {
            return 1;
         }

         if (!p2.sortId || p1.sortId < p2.sortId) {
            return -1;
         }

         return 0;
      });
   }
   private reOrderActivePrompts() {
      this.activeAutoAttendantPromptItems.forEach((promptItem, newSortId) => {
         promptItem.sortId = newSortId + 1;
      });
   }
}
